import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';
import CrudApiClient from './crudapiclient';
import type { SearchQuery } from '../utils';

export type CollectionCsvExportRow = {
  id: string;
  name: string;
  description: string;
  useCase: string;
  sortBy: string;
  limit: string;
  pdpRank: string;
  pdpRankTitle: string;
  pdpRankSubtitle: string;
  headerTitle: string;
  headerTags: string;
  headerDescription: string;
  headerMedia: string;
  quickFilters: string;
  queryFilters: string;
  queryExclusions: string;
  advancedFilters: string;
};

class CollectionApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/catalog/collection');
  }

  async updateById<T = any>(
    id: string | number,
    data: Partial<T>,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      const response = await axiosInstance.put(`${this.baseURL}/${id}`, data, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating collection:', error);
      throw error;
    }
  }

  async deleteById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/${id}`, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw error;
    }
  }

  // Override the transform methods to add custom headers
  async create<T = any>(payload: Partial<T>, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}` + '/', payload, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating collection:', error);
      throw error;
    }
  }

  override async createMany<T = any>(
    payload: Array<Partial<T>>,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/bulkUpsert`, payload, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error bulk upserting collections:', error);
      throw error;
    }
  }

  async exportCsvRows(
    _sorters: unknown,
    _filters: unknown,
    payload: SearchQuery | undefined,
  ): Promise<CollectionCsvExportRow[]> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/export`, payload ?? {}, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responsePayload = response.data?.data ?? response.data;
      const rows = Array.isArray(responsePayload) ? responsePayload : responsePayload?.data;

      if (!Array.isArray(rows)) {
        throw new Error('Collection export response did not contain a row list');
      }
      return rows;
    } catch (error) {
      console.error('Error exporting collections:', error);
      throw error;
    }
  }

  // Method to get facets for filtering
  async getFacets(): Promise<any> {
    try {
      const response = await axiosInstance.post(
        GATEWAY_API_URL + '/search/styles',
        {
          searchType: 'FREETEXT',
          text: '',
          selectedFacets: {},
          page: 1,
          size: 1,
        },
        {
          headers: {
            'x-lk-orgid': 'lyskraft',
            'x-lk-portalcode': 'pos',
            'x-lk-scid': '1',
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data.data.facets || [];
    } catch (error) {
      console.error('Error fetching facets:', error);
      throw error;
    }
  }
}

export default CollectionApiClient;
